import { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Download,
  ChevronDown,
  Filter,
  X,
  EyeOff,
  Eye,
  ChevronUp,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CSVLink } from "react-csv";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPrint } from "@fortawesome/free-solid-svg-icons";
import useOrder from "../hooks/useOrder";
import { useCloud } from "../hooks/useCloud";
import { Reprint } from "../Component/Print/ReprintBill";
import store from "../store/index";
import { formatDate, adjustOrderDate } from "../utils/common";
import PasswordDialog from "../Component/PasswordInput";
import toast from "react-hot-toast";
import { SUCCESS } from "../assets/constants";

const dateToString = (date) => {
  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};
// Utility function for calculating the total amount
const calculateTotalAmount = (orders) => {
  return orders.reduce((acc, order) => acc + parseInt(order.amount), 0);
};

const getOrderStatus = (status) => {
  switch (status) {
    case "settle_later":
      return "Pending";
    case "pending":
      return "Live";
    case "paid":
      return "Paid";
    default:
      return status;
  }
};

const getOredrStatusColor = (status) => {
  switch (status) {
    case "settle_later":
      return "bg-yellow-100 text-yellow-800";
    case "pending":
      return "bg-[#FFA500]  text-white";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-green-100 text-green-800";
  }
};

export default function OrdersReport() {
  const CLOSING_HOURS = 6;
  const PASSWORD = store.restaurantInfo.masterPassword;
  const [searchParams, setSearchParams] = useSearchParams();

  const [showPasswordDialogue, setShowPasswordDialogue] = useState(false);
  const [isProtected, setProtected] = useState(true);
  const [tablesVPAMapping] = useState(
    store.tables.reduce((acc, table) => {
      acc[table.qrpidVPA] = `${table.room} T${table.tableNo}`;
      return acc;
    }, {})
  );
  const [staffMapping] = useState(
    store.staff.reduce((acc, staff) => {
      acc[staff.id] = staff.name;
      return acc;
    }, {})
  );

  const { getOrderDetails, reports } = useCloud();
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [filteredData, setFilteredData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, SetError] = useState(null);
  const [isViewBillOpen, setIsViewBillOpen] = useState(false);
  const [viewBillSelectedItem, SetViewBillSelectedItem] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [dateFilter, setDateFilter] = useState("last30days");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Pagination state - get current page from URL params
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);

  // Function to update page in URL
  const updatePage = (newPage) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  const parseDate = (_date) => {
    const date = new Date(_date);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchReports = async () => {
    setLoading(true);
    const payload = {
      dateType: dateFilter,
      page: currentPage,
    };

    if (dateFilter === "custom") payload["customStart"] = parseDate(startDate);
    payload["customEnd"] = parseDate(endDate);

    try {
      const reports_data = await reports(payload);

      if (reports_data && reports_data.status === SUCCESS) {
        const {
          data: { orders, totalAmountInRange, totalAmountTillNow },
          pagination,
        } = reports_data;
        setOrders(orders);
        setFilteredData(orders);
        setTotalAmount(totalAmountInRange);

        const { currentPage, pageSize, totalItems, totalPages } = pagination;

        setTotalPages(parseInt(totalPages));
        setItemsPerPage(parseInt(pageSize));
        setTotalOrders(parseInt(totalItems));
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong please try again ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [startDate, endDate, dateFilter, searchParams.get("page")]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle bill print
  const handleBillPrint = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      let tableDetails = store.rooms.reduce(
        (acc, room) => {
          const table = room.tables.find(
            (table) => table.qrpidVPA == order.tableVPA
          );
          if (table) {
            return {
              roomName: room.roomName,
              tableNo: table.tableNo,
            };
          }
          return acc;
        },
        {
          roomName: "",
          tableNo: "",
        }
      );
      await Reprint({
        ...order,
        billStatus: getOrderStatus(order.billStatus),
        tableDetails: tableDetails,
      });
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  // Handle view bill
  const handleBillView = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      SetViewBillSelectedItem(order);
      setIsViewBillOpen(true);
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  const filterLastNDays = (days) => {
    const today = new Date();
    const previousDate = new Date();
    previousDate.setDate(today.getDate() - days);
    setStartDate(previousDate);
    setEndDate(today);
  };

  const filterByDate = (date) => {
    setStartDate(new Date(date));
    setEndDate(new Date(date));
  };

  // Clear all filters
  const clearFilters = () => {
    setPaymentMethods([]);
    setDateFilter("");
    setFilteredData(orders);
    setTotalAmount(calculateTotalAmount(orders));
  };

  const unHideRevenue = (credentialResponse) => {
    if (credentialResponse.password === PASSWORD) {
      setProtected(false);
      setShowPasswordDialogue(false);
    } else {
      toast.error("Wrong password");
    }
  };

  return (
    <>
      <PasswordDialog
        isOpen={showPasswordDialogue}
        onClose={() => setShowPasswordDialogue(false)}
        onSubmit={unHideRevenue}
      />
      <div className="bg-gray-50 ">
        <div className="max-w-[1400px] h-[calc(100vh)] w-[100vw] md:w-[calc(100vw-120px)] mx-auto p-4 box-border bg-gray-50 overflow-x-hidden relative hide-scrollbar">
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 h-full overflow-x-scroll hide-scrollbar relative ">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center rounded-t-lg  bg-white flex-wrap gap-2 sticky top-0">
              {loading ? (
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
              ) : (
                <div className="relative">
                  <div className="flex gap-2">
                    <span> Revenue</span>
                    {isProtected ? (
                      <div className="h-8 w-8">
                        <EyeOff
                          className="cursor-pointer hover:stroke-blue-200"
                          onClick={() => setShowPasswordDialogue(true)}
                        />
                      </div>
                    ) : (
                      <div className="h-8 w-8 ">
                        <Eye
                          className="cursor-pointer hover:stroke-blue-200"
                          onClick={() => setProtected(true)}
                        />
                      </div>
                    )}
                  </div>
                  <h2 className="font-semibold text-lg text-gray-900">
                    {isProtected
                      ? "*****"
                      : totalAmount?.toLocaleString("en-IN")}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {totalOrders} Orders
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <div className="relative" ref={filterRef}>
                  <button
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-gray-700  hover:bg-gray-50  transition-colors ${
                      isFilterOpen || paymentMethods.length > 0
                        ? "bg-blue-50 text-blue-700 border-blue-300 border-solid"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </button>
                  {isFilterOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                      <div className="p-4 space-y-4">
                        {/* Date Range */}
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Date Range
                          </label>
                          <div className="flex gap-2 ">
                            <DatePicker
                              selected={startDate}
                              onChange={(date) => {
                                setStartDate(date);
                                setDateFilter("custom");
                              }}
                              className="p-1 border rounded w-[120px]"
                              dateFormat="dd MMM yyyy"
                            />

                            <DatePicker
                              selected={endDate}
                              onChange={(date) => {
                                setEndDate(date);
                                setDateFilter("custom");
                              }}
                              className="p-1 border rounded w-[120px]"
                              dateFormat="dd MMM yyyy"
                            />
                          </div>

                          {/* Quick Date Filters */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {[
                              {
                                label: "Today",
                                id: "today",
                                action: () =>
                                  filterByDate(formatDate(new Date())),
                              },
                              {
                                label: "Yesterday",
                                id: "yesterday",
                                action: () =>
                                  filterByDate(
                                    formatDate(
                                      new Date(
                                        new Date().setDate(
                                          new Date().getDate() - 1
                                        )
                                      )
                                    )
                                  ),
                              },
                              {
                                label: "Last 7 Days",
                                id: "last7days",
                                action: () => filterLastNDays(7),
                              },
                              {
                                label: "Last 30 Days",
                                id: "last30days",
                                action: () => filterLastNDays(30),
                              },
                              {
                                label: "Custom",
                                id: "custom",
                                action: () => filterLastNDays(30),
                              },
                            ].map(({ label, id, action }) => (
                              <button
                                key={label}
                                onClick={() => {
                                  action();
                                  setDateFilter(id);
                                }}
                                className={`px-2 py-1 text-xs
                            ${
                              dateFilter === id
                                ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }
                            rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Payment Methods */}
                        {/* <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Payment Method
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {[...allPaymentTypes].map((method) => (
                              <button
                                key={method}
                                onClick={() =>
                                  handlePaymentMethodToggle(
                                    method.toLowerCase()
                                  )
                                }
                                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                                  paymentMethods.includes(method.toLowerCase())
                                    ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                              >
                                {method === "na" ? "canceled" : method}
                              </button>
                            ))}
                          </div>
                        </div> */}

                        {/* Platform Dropdown */}
                        {/* <div className="space-y-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Platform
                          </label>
                          <div className="relative">
                            <button
                              onClick={() =>
                                setIsPlatformDropdownOpen(
                                  !isPlatformDropdownOpen
                                )
                              }
                              className="w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors hover:bg-gray-50"
                            >
                              <span className="flex items-center justify-between">
                                {platform === "all"
                                  ? "All Platforms"
                                  : platform}
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              </span>
                            </button>

                            {isPlatformDropdownOpen && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                                {["all", "zomato", "swiggy"].map((p) => (
                                  <button
                                    key={p}
                                    onClick={() => {
                                      setPlatform(p);
                                      setIsPlatformDropdownOpen(false);
                                      sudoFilter({
                                        selectedPlatform: p,
                                      });
                                    }}
                                    className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-50 first:rounded-t-md last:rounded-b-md transition-colors"
                                  >
                                    {p === "all" ? "All Platforms" : p}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div> */}
                      </div>
                    </div>
                  )}
                </div>
                {paymentMethods.length > 0 && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear
                  </button>
                )}
                <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors">
                  {/*  */}

                  <CSVLink
                    data={[
                      ...(filteredData.length > 0 ? filteredData : orders),
                      {
                        invoiceNo: "",
                        customerName: "",
                        waiter: "",
                        table: "",
                        state: "",
                        orderDate: "",
                        amount: `Total: RS ${totalAmount}`,
                        paymentmethod: "",
                        tips: "",
                      },
                    ]}
                    headers={[
                      { label: "SI", key: "id" },
                      { label: "Invoice No", key: "invoiceNo" },
                      { label: "Customer Name", key: "customerName" },
                      { label: "Waiter", key: "waiter" },
                      { label: "Table", key: "table" },
                      { label: "State", key: "state" },
                      { label: "Order Date", key: "orderDate" },
                      { label: "Amount", key: "amount" },
                      { label: "Method", key: "paymentmethod" },
                      { label: "Tips", key: "tips" },
                    ]}
                    filename={"orders_with_total_amount.csv"}
                    className="flex gap-1"
                    onClick={(e) => {
                      setLoading(true);
                      setTimeout(() => {
                        // feels link loading
                        setLoading(false);
                      }, 1000);
                      return true;
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Report
                  </CSVLink>
                </button>
              </div>
            </div>
            <div className="overflow-x-scroll">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Waiter
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Table
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tips
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bill
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.length > 0 &&
                    filteredData
                      .sort((a, b) => {
                        const dateDiff =
                          new Date(b.createdAt) - new Date(a.createdAt);
                        if (dateDiff !== 0) return dateDiff;
                        return a.billNo - b.billNo;
                      })
                      .map((order, index) => (
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-[6ch] cursor-default"
                            title={order.billNo} // Tooltip for full invoiceNo
                          >
                            {order.billNo}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-[15ch] cursor-default"
                            title={order.customerName} // Tooltip for full customerName
                          >
                            {order.customerName}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 truncate max-w-[10ch] cursor-default"
                            title={order.staffId} // Tooltip for full waiter
                          >
                            {staffMapping[order.staffId] || "NA"}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-default max-w-[200px] text-wrap"
                            title={order.table} // Tooltip for full table
                          >
                            {order.typeOfOrder === "Takeaway"
                              ? "Parcel"
                              : `${tablesVPAMapping[order.tableVPA]} `}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap flex">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full  ${getOredrStatusColor(
                                order.billStatus
                              )}`}
                            >
                              {order.canceled
                                ? "Cancelled"
                                : getOrderStatus(order.billStatus)}
                            </span>

                            {order.orderType === "digital" && (
                              <span
                                className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 `}
                              >
                                Digital
                              </span>
                            )}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-default"
                            title={`Order time : ${new Date(
                              order.updatedAt
                            ).toLocaleString()}`} // Tooltip for full orderDate
                          >
                            {dateToString(new Date(order.createdAt))}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-default"
                            title={order.amount} // Tooltip for full amount
                          >
                            <div>{parseInt(order.billTxnLogs.total)}</div>
                            {order.paymentmethod === "partial" &&
                              order.partialAmoutsplit?.map((ele, idx) => (
                                <p
                                  key={idx}
                                  title={`${ele.name}: ${parseInt(ele.amount)}`}
                                >
                                  {ele.name}: {parseInt(ele.amount)}
                                </p>
                              ))}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-default"
                            title={order.paymentmethod} // Tooltip for full paymentmethod
                          >
                            {order.paymentmethod}
                          </td>
                          <td
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-default"
                            title={order.billTxnLogs?.tip} // Tooltip for full tips
                          >
                            {order.billTxnLogs?.tip}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <FontAwesomeIcon
                              icon={faEye}
                              className="mr-3"
                              onClick={() => handleBillView(order)}
                            />
                            {!order.canceled && (
                              <FontAwesomeIcon
                                icon={faPrint}
                                className="mr-3"
                                onClick={() => handleBillPrint(order)}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, totalOrders)} of{" "}
                    {totalOrders} results
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => updatePage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  <div className="flex space-x-1">
                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = index + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + index;
                      } else {
                        pageNumber = currentPage - 2 + index;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => updatePage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? "bg-blue-600 text-white"
                              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updatePage(Math.min(currentPage + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* View Bill Modal */}
        {isViewBillOpen && (
          <BillDetailsModal
            viewBillSelectedItem={viewBillSelectedItem}
            setIsViewBillOpen={setIsViewBillOpen}
          />
        )}
      </div>
    </>
  );
}

const BillDetailsModal = ({ viewBillSelectedItem, setIsViewBillOpen }) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Order Items (Bill No: {viewBillSelectedItem.billNo})
          </h2>
          {!viewBillSelectedItem.canceled && (
            <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full  ${getOredrStatusColor(
                viewBillSelectedItem.billStatus
              )}`}
            >
              {getOrderStatus(viewBillSelectedItem.billStatus)}
            </span>
          )}
        </div>

        {viewBillSelectedItem.canceled && (
          <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-100">
            <span className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-red-200 text-red-800 mb-2">
              Cancelled
            </span>
            <p className="text-gray-700">
              Reason: {viewBillSelectedItem?.cancelReason || "Not specified"}
            </p>
          </div>
        )}

        {/* List of order items */}
        <div className="mb-6">
          <div className="grid grid-cols-12 gap-2 py-2 font-medium text-gray-700 border-b border-gray-200">
            <div className="col-span-5">Item</div>
            <div className="col-span-3">Variant</div>
            <div className="col-span-1 text-center">Qty</div>
            <div className="col-span-2 text-right">Price</div>
          </div>

          {viewBillSelectedItem?.items &&
            viewBillSelectedItem?.items.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 py-3 border-b border-gray-100 items-center"
              >
                <div className="col-span-5 font-medium text-gray-800">
                  {item?.name || "Unknown Item"}{" "}
                  {item.isCancelled && (
                    <span className="block  py-0.5 text-xs font-medium rounded-full  text-red-800">
                      Cancelled
                    </span>
                  )}
                </div>
                <div className="col-span-3 text-sm text-gray-600">
                  {item.variantName || "—"}
                </div>
                <div className="col-span-1 text-center text-gray-600">
                  {item?.quantity || "1"}
                </div>
                <div className="col-span-2 text-right font-medium">
                  {Number.parseFloat(item?.price).toFixed(2)}
                </div>
              </div>
            ))}
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 pt-4 space-y-2">
          <div className="flex justify-between text-gray-700">
            <span>Sub Total:</span>
            <span>{viewBillSelectedItem?.billTxnLogs?.subTotal || 0}</span>
          </div>

          {viewBillSelectedItem?.billTxnLogs?.discount > 0 && (
            <div className="flex justify-between text-gray-700">
              <span>Discount:</span>
              <span>{viewBillSelectedItem?.billTxnLogs.discount}</span>
            </div>
          )}

          {viewBillSelectedItem?.billTxnLogs?.gst && (
            <>
              <div className="flex justify-between text-gray-700">
                <span>
                  CGST ({viewBillSelectedItem?.billTxnLogs?.cgstPercent || 0}%):
                </span>
                <span>{viewBillSelectedItem?.billTxnLogs?.cgst || 0}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>
                  SGST ({viewBillSelectedItem?.billTxnLogs?.sgstPercent || 0}%):
                </span>
                <span>{viewBillSelectedItem?.billTxnLogs?.sgst || 0}</span>
              </div>
            </>
          )}

          {viewBillSelectedItem?.billTxnLogs?.tips > 0 && (
            <div className="flex justify-between text-gray-700">
              <span>Tips:</span>
              <span>{viewBillSelectedItem?.billTxnLogs.tips}</span>
            </div>
          )}

          <div className="flex justify-between font-bold text-lg">
            <span>Total Bill:</span>
            <span>{viewBillSelectedItem?.billTxnLogs.total}</span>
          </div>

          {viewBillSelectedItem?.paymentmethod && (
            <div className="flex justify-between text-sm text-gray-600 italic">
              <span>Payment Method:</span>
              <span className="capitalize">
                {viewBillSelectedItem?.paymentmethod}
              </span>
            </div>
          )}
        </div>

        {/* Complete Bill Information (Collapsible) */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            className="flex justify-between items-center w-full text-left font-semibold text-gray-800 focus:outline-none"
            onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          >
            <span>Complete Bill Information</span>
            {isDetailsOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {isDetailsOpen && (
            <div className="bg-gray-50 p-4 rounded-md text-sm mt-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2 pb-2 mb-2 border-b border-gray-200">
                  <span className="font-medium">Bill Details</span>
                </div>

                <div className="text-gray-600">Sub Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.subTotal || 0}
                </div>

                <div className="text-gray-600">Before Discount Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.beforeDiscountTotal || 0}
                </div>

                <div className="text-gray-600">Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.total || 0}
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Discounts</span>
                </div>

                <div className="text-gray-600">Discount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discount || 0}
                </div>

                <div className="text-gray-600">Discount (Flat):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discountFlat || 0}
                </div>

                <div className="text-gray-600">Discount (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discountPercent || 0}%
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Taxes</span>
                </div>

                <div className="text-gray-600">GST Applied:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gst ? "Yes" : "No"}
                </div>

                <div className="text-gray-600">GST Excluded:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gstExcluded
                    ? "Yes"
                    : "No"}
                </div>

                <div className="text-gray-600">GST Number:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gstNo || "N/A"}
                </div>

                <div className="text-gray-600">CGST (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.cgstPercent || 0}%
                </div>

                <div className="text-gray-600">CGST Amount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.cgst || 0}
                </div>

                <div className="text-gray-600">SGST (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.sgstPercent || 0}%
                </div>

                <div className="text-gray-600">SGST Amount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.sgst || 0}
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Other</span>
                </div>

                <div className="text-gray-600">Service Charge:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.service ? "Yes" : "No"}
                </div>

                <div className="text-gray-600">Tip:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.tip || 0}
                </div>

                <div className="text-gray-600">Payment Type:</div>
                <div className="text-right capitalize">
                  {viewBillSelectedItem?.billTxnLogs?.paymentType || "N/A"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          className="mt-6 w-full bg-gray-700 hover:bg-gray-800 text-white py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          onClick={() => setIsViewBillOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
};
